/* eslint-disable complexity */
import moment, {Moment} from 'moment'
import React, {useContext, createContext, useMemo, useReducer, useCallback, useEffect} from 'react'
import {useSearchParams} from 'react-router'

import {
  StatusOption,
  SignalChart,
  SignalTreeViews,
  FilterOption
} from '../../shared/types/plantMonitoring.types'
import {SignalSource, UserSignalGroup} from '../../shared/types/sensor.types'
import {SubjectCategory} from '../hooks/useFeedback'
import {getDatesForItem, QuickSelectionItemId} from '../hooks/useQuickSelectionItems'

const pmEnglishDescriptionsKey = 'pmShowEnglishDescriptions'

export interface PlantMonitoringState {
  signalCharts: SignalChart[]

  chartLayout: {
    order: string[]
    columns: number
  }

  uiLayout: {
    signalTreeCollapsed: boolean
    englishDescription: boolean
  }

  filters: {
    status: StatusOption[]
    providers: SignalSource[]
  }

  timeRange: {
    start: Moment
    end: Moment
    quickSelection: string | null
    zoomStart: Moment | null
    zoomEnd: Moment | null
  }

  feedbackMetaData: FeedbackMetadata | null

  signalTreeView: SignalTreeViews
  scaleToThresholds: boolean
  activeGroup: string | null
}

type FeedbackMetadata = {
  alertInstanceId: string
  subjectCategory: SubjectCategory
  signalStart?: string
  signalEnd?: string
  alertStart?: string
  alertEnd?: string
}

export type PlantMonitoringAction =
  | {type: 'SIGNALS_SELECTED'; payload: string[]}
  | {type: 'CHARTS_REMOVED'; payload: string[]}
  | {type: 'SIGNALS_SET'; payload: string[]}
  | {type: 'CHARTS_REORDERED'; payload: string[]}
  | {type: 'SIGNALS_COMBINED'; payload: string[]}
  | {type: 'COMBINED_CHART_UPDATED'; payload: {chartId: string; signals: string[]}}
  | {type: 'ALL_CHARTS_REMOVED'; payload: undefined}
  | {type: 'SET_SIGNAL_TREE_VIEW'; payload: SignalTreeViews}
  | {type: 'SET_COLUMNS'; payload: number}
  | {type: 'SET_SCALE_TO_THRESHOLDS'; payload: boolean}
  | {type: 'DATE_RANGE_SELECTED'; payload: {startDate: Moment; endDate: Moment}}
  | {type: 'ZOOM_RANGE_SELECTED'; payload: {startDate: Moment | null; endDate: Moment | null}}
  | {
      type: 'QUICK_SELECTION_PICKED'
      payload: {quickSelection: string; startDate: Moment; endDate: Moment}
    }
  | {type: 'CLEAR_ALL_FILTERS'; payload: undefined}
  | {type: 'SET_PROVIDERS_FILTER'; payload: SignalSource[]}
  | {type: 'SET_STATUS_FILTER'; payload: StatusOption[]}
  | {type: 'TOGGLE_PROVIDER'; payload: SignalSource}
  | {type: 'TOGGLE_STATUS'; payload: StatusOption}
  | {type: 'SIGNAL_GROUP_SELECTED'; payload: UserSignalGroup}
  | {type: 'SIGNAL_TREE_COLLAPSE_TOGGLED'; payload: undefined | boolean}
  | {type: 'ENGLISH_DESCRIPTION_TOGGLED'; payload: undefined}

export const initialState: PlantMonitoringState = {
  signalTreeView: SignalTreeViews.SIGNALS,
  scaleToThresholds: true,
  activeGroup: null,
  signalCharts: [],
  chartLayout: {
    order: [],
    columns: 1
  },
  uiLayout: {
    signalTreeCollapsed: false,
    englishDescription: localStorage.getItem(pmEnglishDescriptionsKey) === 'true'
  },
  filters: {
    status: [],
    providers: []
  },
  timeRange: {
    start: moment().subtract(24, 'hours'),
    end: moment(),
    quickSelection: 'last24hours',
    zoomStart: null,
    zoomEnd: null
  },
  feedbackMetaData: null
}

export function plantMonitoringReducer(
  state: PlantMonitoringState,
  action: PlantMonitoringAction
): PlantMonitoringState {
  switch (action.type) {
    case 'SET_SIGNAL_TREE_VIEW':
      return {
        ...state,
        signalTreeView: action.payload
      }
    case 'SET_COLUMNS':
      return {
        ...state,
        chartLayout: {
          ...state.chartLayout,
          columns: action.payload
        }
      }
    case 'SET_SCALE_TO_THRESHOLDS':
      return {
        ...state,
        scaleToThresholds: action.payload
      }
    case 'DATE_RANGE_SELECTED':
      return {
        ...state,
        timeRange: {
          start: action.payload.startDate,
          end: action.payload.endDate,
          zoomStart: null,
          zoomEnd: null,
          quickSelection: null
        },
        feedbackMetaData: null
      }
    case 'ZOOM_RANGE_SELECTED':
      return {
        ...state,
        timeRange: {
          ...state.timeRange,
          zoomStart: action.payload.startDate,
          zoomEnd: action.payload.endDate
        }
      }
    case 'QUICK_SELECTION_PICKED':
      return {
        ...state,
        timeRange: {
          quickSelection: action.payload.quickSelection,
          start: action.payload.startDate,
          end: action.payload.endDate,
          zoomStart: null,
          zoomEnd: null
        },
        feedbackMetaData: null
      }
    case 'CHARTS_REORDERED':
      return {
        ...state,
        chartLayout: {
          ...state.chartLayout,
          order: action.payload
        }
      }
    case 'SIGNALS_COMBINED': {
      const combinedSignalIds = action.payload
      const chartId = generateCombinedChartId(combinedSignalIds)

      // Check if this combination already exists
      const chartExists = state.signalCharts.find((chart) => chart.id === chartId)
      // If chart already exists, just reorder it to the front
      if (chartExists) {
        return {
          ...state,
          feedbackMetaData: null,
          chartLayout: {
            ...state.chartLayout,
            order: [chartId, ...state.chartLayout.order.filter((id) => id !== chartId)]
          }
        }
      }

      return {
        ...state,
        feedbackMetaData: null,
        signalCharts: [
          ...state.signalCharts,
          {
            id: chartId,
            type: 'combined',
            signalIds: combinedSignalIds
          }
        ],
        chartLayout: {
          ...state.chartLayout,
          order: [chartId, ...state.chartLayout.order]
        }
      }
    }
    case 'SIGNALS_SELECTED': {
      const newCharts: SignalChart[] = action.payload
        .filter((signal) => !state.chartLayout.order.includes(signal))
        .map((signalId) => ({
          id: signalId,
          type: 'single',
          signalIds: [signalId]
        }))

      if (newCharts.length === 0) {
        return state
      }
      return {
        ...state,
        feedbackMetaData: null,
        activeGroup: null,
        signalCharts: [...state.signalCharts, ...newCharts],
        chartLayout: {
          ...state.chartLayout,
          order: [...newCharts.map((chart) => chart.id), ...state.chartLayout.order]
        }
      }
    }
    case 'CHARTS_REMOVED': {
      const chartIds = action.payload
      return {
        ...state,
        feedbackMetaData: null,
        activeGroup: null,
        signalCharts: state.signalCharts.filter((chart) => !chartIds.includes(chart.id)),
        chartLayout: {
          ...state.chartLayout,
          order: state.chartLayout.order.filter((id) => !chartIds.includes(id))
        }
      }
    }
    case 'SIGNALS_SET': {
      const newCharts: SignalChart[] = action.payload.map((signalId) => ({
        id: signalId,
        type: 'single',
        signalIds: [signalId]
      }))
      const combinedChartIds = state.signalCharts
        .filter((chart) => chart.type === 'combined')
        .map((chart) => chart.id)
      return {
        ...state,
        feedbackMetaData: null,
        activeGroup: null,
        signalCharts: [
          // Keep the combined charts but remove the rest
          ...state.signalCharts.filter((chart) => chart.type === 'combined'),
          ...newCharts
        ],
        chartLayout: {
          ...state.chartLayout,
          order: [
            ...newCharts.map((chart) => chart.id),
            ...state.chartLayout.order.filter((id) => combinedChartIds.includes(id))
          ]
        }
      }
    }
    case 'COMBINED_CHART_UPDATED': {
      const {chartId, signals} = action.payload
      const newChartId = generateCombinedChartId(signals)

      // If ID changes, we need to update both chart and order
      const updatedCharts = state.signalCharts.map((chart) => {
        if (chart.id === chartId) {
          return {
            ...chart,
            id: newChartId,
            signalIds: signals
          }
        }
        return chart
      })

      const updatedOrder = state.chartLayout.order.map((id) => (id === chartId ? newChartId : id))

      return {
        ...state,
        signalCharts: updatedCharts,
        chartLayout: {
          ...state.chartLayout,
          order: updatedOrder
        }
      }
    }
    case 'TOGGLE_PROVIDER':
      return {
        ...state,
        filters: {
          ...state.filters,
          providers: state.filters.providers.includes(action.payload)
            ? state.filters.providers.filter((provider) => provider !== action.payload)
            : [...state.filters.providers, action.payload]
        }
      }
    case 'TOGGLE_STATUS':
      return {
        ...state,
        filters: {
          ...state.filters,
          status: state.filters.status.includes(action.payload)
            ? state.filters.status.filter((status) => status !== action.payload)
            : [...state.filters.status, action.payload]
        }
      }
    case 'CLEAR_ALL_FILTERS':
      return {
        ...state,
        filters: {
          providers: [],
          status: []
        }
      }
    case 'SET_PROVIDERS_FILTER':
      return {
        ...state,
        filters: {
          ...state.filters,
          providers: action.payload
        }
      }
    case 'SET_STATUS_FILTER':
      return {
        ...state,
        filters: {
          ...state.filters,
          status: action.payload
        }
      }
    case 'SIGNAL_GROUP_SELECTED': {
      const {payload: userGroup} = action
      const newSingleCharts: SignalChart[] = userGroup.signals.map((signalId) => ({
        id: signalId,
        type: 'single',
        signalIds: [signalId]
      }))

      const newCombinedCharts: SignalChart[] = userGroup.combined.map((signals) => ({
        id: generateCombinedChartId(signals),
        type: 'combined',
        signalIds: signals
      }))

      const newCharts = [...newSingleCharts, ...newCombinedCharts]

      const [startDate, endDate] = getDatesForItem(
        (userGroup.quickSelection ?? 'last24hours') as QuickSelectionItemId
      )

      return {
        ...state,
        feedbackMetaData: null,
        activeGroup: userGroup.name,
        signalCharts: newCharts,
        chartLayout: {
          ...state.chartLayout,
          columns: userGroup.columns,
          order: userGroup.chartsOrder.length
            ? userGroup.chartsOrder
            : newCharts.map((chart) => chart.id)
        },
        ...(userGroup.quickSelection && {
          timeRange: {
            quickSelection: userGroup.quickSelection,
            start: moment(startDate),
            end: moment(endDate),
            zoomStart: null,
            zoomEnd: null
          }
        })
      }
    }

    case 'ALL_CHARTS_REMOVED':
      return {
        ...state,
        feedbackMetaData: null,
        activeGroup: null,
        signalCharts: [],
        chartLayout: {
          ...state.chartLayout,
          order: []
        }
      }
    case 'SIGNAL_TREE_COLLAPSE_TOGGLED':
      return {
        ...state,
        uiLayout: {
          ...state.uiLayout,
          signalTreeCollapsed:
            action.payload === undefined ? !state.uiLayout.signalTreeCollapsed : action.payload
        }
      }
    case 'ENGLISH_DESCRIPTION_TOGGLED': {
      const newValue = !state.uiLayout.englishDescription
      localStorage.setItem(pmEnglishDescriptionsKey, newValue.toString())

      return {
        ...state,
        uiLayout: {
          ...state.uiLayout,
          englishDescription: newValue
        }
      }
    }
    default: {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _exhaustiveCheck: never = action
      return state
    }
  }
}

interface PlantMonitoringContext {
  state: PlantMonitoringState
  actions: {
    setSignalTreeView: (view: SignalTreeViews) => void
    setSelectedSignals: (signals: string[]) => void
    setColumns: (columns: number) => void
    setScaleToThresholds: (scale: boolean) => void
    setDateRange: (startDate: Moment, endDate: Moment) => void
    setZoomRange: (startDate: Moment | null, endDate: Moment | null) => void
    setQuickSelection: (selection: string, startDate: Moment, endDate: Moment) => void
    toggleProvider: (provider: SignalSource) => void
    toggleStatus: (status: StatusOption) => void
    clearAllFilters: () => void
    clearFilter: (filter: FilterOption) => void
    selectSignalGroup: (group: UserSignalGroup) => void
    selectSignal: (signalId: string) => void
    selectSignals: (signalId: string[]) => void
    deselectSignal: (signalId: string) => void
    deselectSignals: (signalIds: string[]) => void
    removeChart: (chartId: string) => void
    reorderCharts: (order: string[]) => void
    combineSignals: (signalIds: string[]) => void
    updateCombinedChart: (chartId: string, signals: string[]) => void
    clearAllCharts: () => void
    collapseSignalTree: (collapsed?: boolean) => void
    toggleEnglishDescription: () => void
  }
}

export const PlantMonitoringContext = createContext<PlantMonitoringContext>(
  null as unknown as PlantMonitoringContext
)

const stateToUrlParams = (state: PlantMonitoringState): URLSearchParams => {
  const params = new URLSearchParams()

  const single = state.signalCharts.filter((chart) => chart.type === 'single')
  const combined = state.signalCharts.filter((chart) => chart.type === 'combined')

  // Signal selection
  single.length && params.set('selected', single.flatMap((chart) => chart.signalIds).join(','))
  combined.length &&
    params.set('combined', JSON.stringify(combined.map((chart) => chart.signalIds)))

  // Chart layout
  params.set('columns', String(state.chartLayout.columns))
  state.chartLayout.order.length && params.set('order', state.chartLayout.order.join(','))
  params.set('scaleToThresholds', String(state.scaleToThresholds))

  // Add time range
  state.timeRange.start && params.set('start', state.timeRange.start.toISOString())
  state.timeRange.end && params.set('end', state.timeRange.end.toISOString())
  state.timeRange.quickSelection && params.set('quickSelection', state.timeRange.quickSelection)

  return params
}

const parseUrlParams = (
  params: URLSearchParams,
  initialState: PlantMonitoringState
): PlantMonitoringState => {
  const urlQuickSelection = params.get('quickSelection')
  const urlColumns = params.get('columns')
  const urlScaleToThresholds = params.get('scaleToThresholds')
  const urlSelected = params.get('selected')
  const urlCombined = params.get('combined')
  const urlOrder = params.get('order')
  const urlStart = params.get('start')
  const urlEnd = params.get('end')

  // feedback metadata
  const urlAlertInstanceId = params.get('alertInstanceId')
  const urlSubjectCategory = params.get('subjectCategory')
  const urlSignalStart = params.get('signalStart')
  const urlSignalEnd = params.get('signalEnd')

  try {
    const selected = urlSelected ? urlSelected.split(',') : []
    const combined: string[][] = urlCombined ? JSON.parse(urlCombined) : []
    const order = urlOrder ? urlOrder.split(',') : []

    const newSingleCharts: SignalChart[] = selected.map((signalId) => ({
      id: signalId,
      type: 'single',
      signalIds: [signalId]
    }))

    const newCombinedCharts: SignalChart[] = combined.map((signals) => ({
      id: generateCombinedChartId(signals),
      type: 'combined',
      signalIds: signals
    }))

    const newCharts = [...newSingleCharts, ...newCombinedCharts]

    const effectiveQuickSelection = urlQuickSelection ?? 'last24hours'
    const useQuickSelecton = urlQuickSelection !== null || urlStart === null || urlEnd === null

    const [quickSelectionStart, quickSelectionEnd] = getDatesForItem(
      effectiveQuickSelection as QuickSelectionItemId
    )

    return {
      ...initialState,
      signalCharts: newCharts,
      scaleToThresholds: urlScaleToThresholds
        ? urlScaleToThresholds === 'true'
        : initialState.scaleToThresholds,
      chartLayout: {
        ...initialState.chartLayout,
        columns: urlColumns ? parseInt(urlColumns) : initialState.chartLayout.columns,
        order: order.length ? order : newCharts.map((chart) => chart.id)
      },
      timeRange: {
        quickSelection: useQuickSelecton ? effectiveQuickSelection : null,
        start: moment(useQuickSelecton ? quickSelectionStart : urlStart),
        end: moment(useQuickSelecton ? quickSelectionEnd : urlEnd),
        zoomStart: null,
        zoomEnd: null
      },
      feedbackMetaData: urlAlertInstanceId
        ? {
            alertInstanceId: urlAlertInstanceId,
            subjectCategory: urlSubjectCategory as SubjectCategory,
            signalStart: urlSignalStart ?? undefined,
            signalEnd: urlSignalEnd ?? undefined,
            alertStart: urlStart ?? undefined,
            alertEnd: urlEnd ?? undefined
          }
        : null
    }
  } catch (error) {
    console.error('Error parsing URL parameters:', error)
    return initialState
  }
}

export const PlantMonitoringProvider = ({
  children,
  isWidget
}: {
  children: React.ReactNode
  isWidget?: boolean
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [state, dispatch] = useReducer(
    plantMonitoringReducer,
    parseUrlParams(searchParams, initialState)
  )

  useEffect(() => {
    if (isWidget) return
    setSearchParams(stateToUrlParams(state))
  }, [isWidget, setSearchParams, state])

  // Action creators for convenience
  const actions: PlantMonitoringContext['actions'] = useMemo(
    () => ({
      selectSignal: (signalId: string) => dispatch({type: 'SIGNALS_SELECTED', payload: [signalId]}),
      selectSignals: (signalIds: string[]) =>
        dispatch({type: 'SIGNALS_SELECTED', payload: signalIds}),

      // Deselecting a signal is the same as removing its chart
      deselectSignal: (signalId: string) => dispatch({type: 'CHARTS_REMOVED', payload: [signalId]}),
      deselectSignals: (signalIds: string[]) =>
        dispatch({type: 'CHARTS_REMOVED', payload: signalIds}),
      removeChart: (chartId: string) => dispatch({type: 'CHARTS_REMOVED', payload: [chartId]}),

      setSignalTreeView: (view: SignalTreeViews) =>
        dispatch({type: 'SET_SIGNAL_TREE_VIEW', payload: view}),

      setSelectedSignals: (signals: string[]) => dispatch({type: 'SIGNALS_SET', payload: signals}),

      setColumns: (columns: number) => dispatch({type: 'SET_COLUMNS', payload: columns}),

      setScaleToThresholds: (scale: boolean) =>
        dispatch({type: 'SET_SCALE_TO_THRESHOLDS', payload: scale}),

      setDateRange: (startDate: Moment, endDate: Moment) =>
        dispatch({type: 'DATE_RANGE_SELECTED', payload: {startDate, endDate}}),

      setZoomRange: (startDate: Moment | null, endDate: Moment | null) =>
        dispatch({type: 'ZOOM_RANGE_SELECTED', payload: {startDate, endDate}}),

      setQuickSelection: (selection: string, startDate: Moment, endDate: Moment) =>
        dispatch({
          type: 'QUICK_SELECTION_PICKED',
          payload: {quickSelection: selection, startDate, endDate}
        }),

      toggleProvider: (provider: SignalSource) =>
        dispatch({type: 'TOGGLE_PROVIDER', payload: provider}),

      toggleStatus: (status: StatusOption) => dispatch({type: 'TOGGLE_STATUS', payload: status}),

      clearFilter: (filter: FilterOption) => {
        if (filter === 'Status') {
          dispatch({type: 'SET_STATUS_FILTER', payload: []})
        }
        if (filter === 'Provider') {
          dispatch({type: 'SET_PROVIDERS_FILTER', payload: []})
        }
      },

      clearAllFilters: () => dispatch({type: 'CLEAR_ALL_FILTERS', payload: undefined}),

      selectSignalGroup: (group: UserSignalGroup) =>
        dispatch({type: 'SIGNAL_GROUP_SELECTED', payload: group}),

      reorderCharts: (order: string[]) => dispatch({type: 'CHARTS_REORDERED', payload: order}),

      combineSignals: (signalIds: string[]) =>
        dispatch({type: 'SIGNALS_COMBINED', payload: signalIds}),

      updateCombinedChart: (chartId: string, signals: string[]) =>
        dispatch({type: 'COMBINED_CHART_UPDATED', payload: {chartId, signals}}),

      clearAllCharts: () => dispatch({type: 'ALL_CHARTS_REMOVED', payload: undefined}),

      collapseSignalTree: (collapsed?: boolean) =>
        dispatch({type: 'SIGNAL_TREE_COLLAPSE_TOGGLED', payload: collapsed}),

      toggleEnglishDescription: () =>
        dispatch({type: 'ENGLISH_DESCRIPTION_TOGGLED', payload: undefined})
    }),
    []
  )

  const value: PlantMonitoringContext = {
    state,
    actions
  }

  return (
    <PlantMonitoringContext.Provider value={value}>
      <PlantMonitoringSelectorsProvider>{children}</PlantMonitoringSelectorsProvider>
    </PlantMonitoringContext.Provider>
  )
}

export const usePlantMonitoring = () => {
  const context = useContext(PlantMonitoringContext)
  if (!context) {
    throw new Error('usePlantMonitoring must be used within PlantMonitoringContextProvider')
  }
  return context
}

interface PlantMonitoringSelectorsContextValue {
  selectedIds: string[]
  effectiveColumns: number
  isSignalSelected: (signalId: string) => boolean
  getChartById: (id: string) => SignalChart | undefined
  groupDataFromState: Pick<
    UserSignalGroup,
    'chartsOrder' | 'columns' | 'quickSelection' | 'combined' | 'type'
  >
}

const PlantMonitoringSelectorsContext = createContext<PlantMonitoringSelectorsContextValue>(
  null as unknown as PlantMonitoringSelectorsContextValue
)

export const PlantMonitoringSelectorsProvider = ({children}: {children: React.ReactNode}) => {
  const {state} = usePlantMonitoring()

  const selectedLocalNames = useMemo(
    () =>
      new Set(
        state.signalCharts
          .filter((chart) => chart.type === 'single')
          .flatMap((chart) => chart.signalIds)
      ),
    [state.signalCharts]
  )

  const effectiveColumns = useMemo(
    () => Math.min(state.chartLayout.columns, state.signalCharts.length || 1),
    [state.chartLayout.columns, state.signalCharts.length]
  )

  const isSignalSelected = useCallback(
    (signalId: string) => selectedLocalNames.has(signalId),
    [selectedLocalNames]
  )

  const getChartById = useCallback(
    (id: string) => state.signalCharts.find((chart) => chart.id === id),
    [state.signalCharts]
  )

  const groupDataFromState: Pick<
    UserSignalGroup,
    'chartsOrder' | 'columns' | 'quickSelection' | 'combined' | 'type'
  > = useMemo(
    () => ({
      chartsOrder: state.chartLayout.order,
      columns: state.chartLayout.columns,
      quickSelection: state.timeRange.quickSelection,
      type: 'user',
      combined: state.signalCharts
        .filter((chart) => chart.type === 'combined')
        .map((chart) => chart.signalIds)
    }),
    [
      state.chartLayout.order,
      state.chartLayout.columns,
      state.timeRange.quickSelection,
      state.signalCharts
    ]
  )

  const value: PlantMonitoringSelectorsContextValue = useMemo(
    () => ({
      selectedIds: Array.from(selectedLocalNames),
      effectiveColumns,
      isSignalSelected,
      getChartById,
      groupDataFromState
    }),
    [selectedLocalNames, effectiveColumns, isSignalSelected, getChartById, groupDataFromState]
  )

  return (
    <PlantMonitoringSelectorsContext.Provider value={value}>
      {children}
    </PlantMonitoringSelectorsContext.Provider>
  )
}

export const usePlantMonitoringSelectors = () => {
  const context = useContext(PlantMonitoringSelectorsContext)
  if (!context) {
    throw new Error(
      'usePlantMonitoringSelectors must be used within PlantMonitoringSelectorsProvider'
    )
  }
  return context
}

export const generateCombinedChartId = (signalIds: string[]): string => {
  // Sort to ensure consistent ID regardless of selection order
  const sortedIds = [...signalIds].sort()
  return `combined-${sortedIds.join('-')}`
}
